/* 
eslint-disable no-mixed-operators
*/
import React from 'react';
import dlv from 'dlv';
import Box from '../layout/box';
import Text from '../text';
import { isString } from '../../../util';
import { errorToString } from '../../../util/validation';
import debounce from 'debounce';
import { makeStyles } from '../../theme';
import Dialog, { useDialog } from '../dialog';

const Form = React.memo(
	({children, onSubmit, className, action, method, onForm, ...props}) => {
		const formRef = React.useRef();
		const [ focused, setFocused ] = React.useState(false);
		const handleFocus = () => setFocused(true);
		const handleBlur = () => setFocused(false);
		const noOp = e => e.preventDefault();
		React.useEffect(() => {
			const handleEnterKey = event => {
				if(focused && event.keyCode === 13) {
					event.preventDefault();
					event.stopImmediatePropagation();
					if(onSubmit) onSubmit();
				}
			};
			window.addEventListener('keyup', handleEnterKey);
			return () => window.removeEventListener('keyup', handleEnterKey);
		});
		React.useEffect(() => {
			if(onForm)onForm(formRef);
		}, [formRef, onForm]);

		if(action) {
			action = action.replace(/&amp;/g, "&");
		}
		
		return (
			<form ref={formRef} onFocus={handleFocus} onBlur={handleBlur} onSubmit={noOp} action={action} method={method} className={className}>
				<Box {...props}>
					{children}
				</Box>
			</form>
			
		);
	}
);

export default Form;

/**
 * Use `theme settings` as much as possible instead of hard-coded values.
 * More theme settings on: https://material-ui.com/customization/theming/
 */
const generalErrTxtStyles = makeStyles(
	({ palette, spacing }) => ({
		generalErrMsg: {
			color: palette.error.main,
			marginBottom: spacing(2.5)
		}
	})
);

export const GeneralErrorText = React.memo(({ msg }) => {
	const classes = generalErrTxtStyles();
	return msg ? (<Text className={classes.generalErrMsg}>{msg}</Text>) : null;
});

export function ResultNotifyPrompt({notification, service, ...props}) {
	const [ open, openDialog, closeDialog ] = useDialog();
	const [lastNotification, setLastNotification] = React.useState();
	if((notification && !lastNotification) || (notification && lastNotification && notification.id !== lastNotification.id)) {
		if(!service || service === notification.service) setLastNotification(notification);
	}
	React.useEffect(() => {
		openDialog();
	},[lastNotification]); //eslint-disable-line react-hooks/exhaustive-deps

	if(lastNotification) return (
		<Dialog
			open={open}
			title={notification.title}
			body={notification.message}
			confirmBtnTxt={notification.confirm}
			onConfirm={closeDialog}
			/>
	);
	return null;
}

export function useFormData() {
	const [state, setState] = React.useState({});

	const setField = (fieldName, value) => {
		if(isString(value))value = value.trim();
		if(state[fieldName] !== value) setState({...state, [fieldName]:value});
	}

	const bindField = (fieldName, fn) => (event) => {
		const value = fn ? fn(event) : event.target ? event.target.value : event;
		setField(fieldName, value);
	};

	
	const bindHidden = (fieldName, defaults) => {
		//if(state[fieldName] === undefined) setField(fieldName, defaults.defaultValue || '');

		return {
			id:fieldName,
			name:fieldName,
			value:defaults.defaultValue || ''
		}
	};
	
	const bindObjectEditor= (fieldName, defaults) => {
		if(state[fieldName] === undefined) setField(fieldName, defaults.defaultValue);
		return {
			id:fieldName,
			name:fieldName,
			onChange:bindField(fieldName, (event) => {
				return event;
			}),
			...defaults
		}
	};
	
	const bindTextfield = (fieldName, defaults, errors) => {
		const errorMessage = dlv(errors, fieldName);
		const applyError = errorMessage ? { error:true, helperText:errorToString(errorMessage) }: {};

		if(state[fieldName] === undefined) setField(fieldName, defaults.defaultValue || '');

		return {
			id:fieldName,
			name:fieldName,
			onChange:bindField(fieldName),
			...defaults,
			...applyError
		}
	};

	const bindCheckbox = (fieldName, defaults) => {
		const defaultValue = dlv(defaults, fieldName) || false;
		return {
			id:fieldName,
			name:fieldName,
			onChange:bindField(fieldName, event => event.target.checked),
			defaultChecked:!!defaultValue
		}
	};

	const bindRadioGroup = (fieldName, defaults) => {
		const defaultValue = dlv(defaults, fieldName);
		if(!state[fieldName] && defaultValue)setField(fieldName, defaultValue);
		return {
			id:fieldName,
			name:fieldName,
			defaultValue:defaultValue,
			onChange:bindField(fieldName)
		}
	};

	const bindSelectList = (fieldName, defaults, options = []) => {
		const defaultValue = dlv(defaults, fieldName, options[0] && options[0].value ||  options[0]);

		if(!state[fieldName] && defaultValue){
			setField(fieldName, defaultValue, defaultValue);
		};
		return {
			id:fieldName,
			defaultValue,
			options,
			onChange:bindField(fieldName, value => value)
		}
	};

	const bindRecaptcha = (fieldName) => {
		if(state[fieldName] === undefined)setField(fieldName, "");
		return {
			id:fieldName,
			onChange:bindField(fieldName)
		}
	};

	return [state, { 
		setField, 
		bindField,
		bindTextfield,
		bindCheckbox,
		bindRadioGroup,
		bindSelectList,
		bindRecaptcha,
		bindHidden,
		bindObjectEditor
	}];
}

export function useValidate(rules) {
	const [errors, setErrors] = React.useState();
	const validate = (formData, includedFields) => {
		const useRules = (includedFields && includedFields.length ) ? rules.filter(rule => includedFields.indexOf(rule.field) > -1) : rules;
		const errors = useRules.reduce((acc, rule) => {
			const r = rule.validate(formData, rule.field, rule.required !== undefined ? rule.required : true);
			if(formData[rule.field] !== undefined && r !== true) {
				const useField = rule.errorAs || rule.field;
				if(rule.field) {
					if(!acc[useField]) acc[useField] = [];
					acc[useField].push(r);
				} else {
					Object.keys(r).forEach(k => {
						if(!acc[k]) acc[k] = [];
						acc[k].push(r[k]);
					});

				}	
			}
			return acc;
		}, {});
		setErrors(errors);
		return Object.keys(errors).length === 0;

	}
	return [errors, validate];
}

export function Validator({ children, fieldValidations, onSubmit, errors }) {
	const [internalerrors, validate] = useValidate(fieldValidations);
	const handleSubmit = data => validate(data) && onSubmit && onSubmit(data);
	const form = React.Children.only(children);
	return React.cloneElement(form, { 
		onSubmit:handleSubmit, 
		errors: {...internalerrors, ...errors }
	});
}

export function validator(fieldValidations) {
	return function (Child) {
		function Wrapper({onSubmit, errors, ...props}) {
			return (
				<Validator fieldValidations={fieldValidations} onSubmit={onSubmit} errors={errors}>
					<Child {...props}/>
				</Validator>
			);
		}
		return Wrapper;
	};
}

export function rule(field, validate, required, errorAs) {
	return { field, validate: validate, required, errorAs };
}

export function fieldsForService(serviceId, serviceDescriptors) {
	if(!serviceId || !serviceDescriptors) return;
	return serviceDescriptors.reduce((acc, desc) => {
		return (desc.id === serviceId && desc.fields) ? [...acc, ...desc.fields] : acc;
	}, []);
}

export function useSubmit(formData, submitFn, beforeSubmitFn) {
	const submit = debounce((serviceId, formQuery) => {
		beforeSubmitFn && beforeSubmitFn();
		submitFn && submitFn(formData, serviceId, formQuery);
	}, 100);

	return submit;
}

export function collectErrors() {
	return [...arguments].reduce((acc, errorObj) => {
		if(errorObj) {
			Object.keys(errorObj).forEach(errorKey => {
				if(!acc[errorKey]) acc[errorKey] = [];
				if(Array.isArray(errorObj[errorKey])) {
					errorObj[errorKey].forEach(err => acc[errorKey].push(err));
				} else {
					acc[errorKey].push(errorObj[errorKey]);
				}
			});
		}
		return acc;
	}, {});
}